import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, type Engagement, type Finding } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, Money, PageHead, Severity, Skel, Stat } from '../components/ui'
import { thaiDate } from '../lib/format'

type CardStatus = 'active' | 'frozen' | 'closed'

interface CardRow {
  id: string
  holder: string
  last4: string
  limit_satang: number
  status: CardStatus
  issued_on: string
}

interface CardPolicy {
  blocked_mcc: string[]
  night_start_hour: number
  night_end_hour: number
  single_txn_limit_satang: number
}

interface CardScan {
  generated_at: string
  engagement_id: string
  cards: number
  scanned: number
  hits: Finding[]
  created: number
  skipped: number
}

interface CardDraft {
  id: string
  holder: string
  last4: string
  limit: string
  status: CardStatus
  issued_on: string
}

interface CardPayload {
  id: string
  holder: string
  last4: string
  limit_satang: number
  status: CardStatus
  issued_on: string
}

const EMPTY_DRAFT: CardDraft = {
  id: '', holder: '', last4: '', limit: '', status: 'active', issued_on: '',
}

const DEFAULT_POLICY: CardPolicy = {
  blocked_mcc: [],
  night_start_hour: 21,
  night_end_hour: 7,
  single_txn_limit_satang: 5_000_000,
}

const STATUS_LABEL: Record<CardStatus, string> = {
  active: 'ใช้งาน',
  frozen: 'อายัด',
  closed: 'ปิดบัตร',
}

const STATUS_TONE: Record<CardStatus, string> = {
  active: 'var(--color-emerald-soft)',
  frozen: 'var(--color-amber)',
  closed: 'var(--color-critical)',
}

const HOURS = Array.from({ length: 24 }, (_, h) => h)

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** Parses a baht string into integer satang without ever going through a float. */
function toSatang(input: string): number | null {
  const s = input.trim().replace(/,/g, '')
  if (s === '' || !/^\d+(\.\d{0,2})?$/.test(s)) return null
  const [whole, frac = ''] = s.split('.')
  const value = Number(whole) * 100 + Number(`${frac}00`.slice(0, 2))
  return Number.isSafeInteger(value) ? value : null
}

function fromSatang(satang: number): string {
  return (satang / 100).toFixed(2)
}

export function Cards() {
  const { me } = useAuth()
  const canWrite = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const qc = useQueryClient()

  const cardsQ = useQuery({ queryKey: ['cards'], queryFn: () => api<CardRow[]>('/v1/cards') })
  const policyQ = useQuery({ queryKey: ['card-policy'], queryFn: () => api<CardPolicy>('/v1/cards/policy') })
  const engQ = useQuery({ queryKey: ['engagements'], queryFn: () => api<Engagement[]>('/v1/engagements') })

  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<CardDraft>(EMPTY_DRAFT)
  const [cardErr, setCardErr] = useState('')

  const [policyDraft, setPolicyDraft] = useState<CardPolicy>(DEFAULT_POLICY)
  const [limitText, setLimitText] = useState(fromSatang(DEFAULT_POLICY.single_txn_limit_satang))
  const [mccInput, setMccInput] = useState('')
  const [policyErr, setPolicyErr] = useState('')
  const [policySaved, setPolicySaved] = useState(false)

  const [scanEngagement, setScanEngagement] = useState('')
  const [scanErr, setScanErr] = useState('')

  useEffect(() => {
    if (!policyQ.data) return
    setPolicyDraft(policyQ.data)
    setLimitText(fromSatang(policyQ.data.single_txn_limit_satang))
  }, [policyQ.data])

  const saveCard = useMutation({
    mutationFn: (p: CardPayload) => apiWrite<CardRow>('/v1/cards', 'POST', p),
    onSuccess: () => {
      setOpen(false)
      setDraft(EMPTY_DRAFT)
      setCardErr('')
      void qc.invalidateQueries({ queryKey: ['cards'] })
    },
    onError: (e) => setCardErr((e as Error).message),
  })

  const savePolicy = useMutation({
    mutationFn: (p: CardPolicy) => apiWrite<CardPolicy>('/v1/cards/policy', 'PUT', p),
    onSuccess: (res) => {
      setPolicyDraft(res)
      setLimitText(fromSatang(res.single_txn_limit_satang))
      setPolicyErr('')
      setPolicySaved(true)
      void qc.invalidateQueries({ queryKey: ['card-policy'] })
    },
    onError: (e) => { setPolicySaved(false); setPolicyErr((e as Error).message) },
  })

  const scan = useMutation({
    mutationFn: (engagementId: string) => apiWrite<CardScan>('/v1/cards/scan', 'POST', { engagement_id: engagementId }),
    onSuccess: () => {
      setScanErr('')
      void qc.invalidateQueries({ queryKey: ['findings'] })
    },
    onError: (e) => setScanErr((e as Error).message),
  })

  function startCreate() {
    if (open && draft.id === '') {
      setOpen(false)
      return
    }
    setDraft(EMPTY_DRAFT)
    setCardErr('')
    setOpen(true)
  }

  function startEdit(c: CardRow) {
    setDraft({
      id: c.id, holder: c.holder, last4: c.last4, limit: fromSatang(c.limit_satang),
      status: c.status, issued_on: c.issued_on,
    })
    setCardErr('')
    setOpen(true)
  }

  function submitCard() {
    const satang = toSatang(draft.limit)
    if (satang === null) {
      setCardErr('วงเงินต้องเป็นตัวเลขทศนิยมไม่เกิน 2 ตำแหน่ง')
      return
    }
    setCardErr('')
    saveCard.mutate({
      id: draft.id,
      holder: draft.holder.trim(),
      last4: draft.last4.trim(),
      limit_satang: satang,
      status: draft.status,
      issued_on: draft.issued_on,
    })
  }

  function addMCC() {
    const code = mccInput.trim()
    if (!/^\d{4}$/.test(code)) {
      setPolicyErr('รหัส MCC ต้องเป็นตัวเลข 4 หลัก')
      return
    }
    setPolicyErr('')
    setPolicySaved(false)
    if (!policyDraft.blocked_mcc.includes(code)) {
      setPolicyDraft({ ...policyDraft, blocked_mcc: [...policyDraft.blocked_mcc, code].sort() })
    }
    setMccInput('')
  }

  function removeMCC(code: string) {
    setPolicySaved(false)
    setPolicyDraft({ ...policyDraft, blocked_mcc: policyDraft.blocked_mcc.filter((m) => m !== code) })
  }

  function submitPolicy() {
    const satang = toSatang(limitText)
    if (satang === null) {
      setPolicyErr('วงเงินต่อรายการต้องเป็นตัวเลขทศนิยมไม่เกิน 2 ตำแหน่ง')
      return
    }
    setPolicyErr('')
    setPolicySaved(false)
    savePolicy.mutate({ ...policyDraft, single_txn_limit_satang: satang })
  }

  const rows = cardsQ.data ?? []
  const activeCount = rows.filter((c) => c.status === 'active').length
  const totalLimit = rows.reduce((sum, c) => sum + c.limit_satang, 0)
  const engagements = engQ.data ?? []
  const engagementID = scanEngagement || engagements[0]?.id || ''

  return (
    <>
      <PageHead
        title="บัตรองค์กร"
        sub="ทะเบียนบัตรและนโยบายร้านค้า ตรวจวงเงิน หมวดธุรกิจ เวลาที่ห้ามใช้ และบัตรที่ถูกระงับ"
        actions={canWrite ? (
          <button className="btn btn-primary" onClick={startCreate}>
            {open ? 'ปิดฟอร์ม' : 'เพิ่มบัตร'}
          </button>
        ) : undefined}
      />

      <div className="grid-3" style={{ marginBottom: 16 }}>
        <Stat label="บัตรทั้งหมด" value={rows.length} unit="ใบ" />
        <Stat label="ใช้งานอยู่" value={activeCount} unit="ใบ" foot={`อายัดหรือปิด ${rows.length - activeCount} ใบ`} />
        <Stat label="วงเงินรวม" value={<Money satang={totalLimit} compact />} foot="รวมวงเงินต่อรายการของทุกบัตร" />
      </div>

      {open && (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="card-label">{draft.id ? `แก้ไขบัตร · ••••${draft.last4}` : 'เพิ่มบัตรใหม่'}</div>
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-holder">ชื่อผู้ถือบัตร</label>
              <input id="c-holder" className="input" value={draft.holder} maxLength={200}
                onChange={(e) => setDraft({ ...draft, holder: e.target.value })}
                placeholder="สมชาย ใจดี" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-last4">เลขท้ายบัตร 4 หลัก</label>
              <input id="c-last4" className="input mono" inputMode="numeric" maxLength={4} value={draft.last4}
                onChange={(e) => setDraft({ ...draft, last4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                placeholder="4410" />
              <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>ระบบเก็บเฉพาะ 4 หลักสุดท้าย ไม่รับเลขบัตรเต็ม</div>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-limit">วงเงินต่อรายการ (บาท)</label>
              <input id="c-limit" className="input mono" inputMode="decimal" value={draft.limit}
                onChange={(e) => setDraft({ ...draft, limit: e.target.value.replace(/[^0-9.]/g, '').slice(0, 15) })}
                placeholder="50,000.00" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-status">สถานะบัตร</label>
              <select id="c-status" className="select" style={{ width: '100%' }} value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value as CardStatus })}>
                <option value="active">ใช้งาน</option>
                <option value="frozen">อายัด</option>
                <option value="closed">ปิดบัตร</option>
              </select>
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-issued">วันที่ออกบัตร</label>
              <input id="c-issued" className="input mono" type="date" value={draft.issued_on}
                onChange={(e) => setDraft({ ...draft, issued_on: e.target.value })} />
            </div>
          </div>
          {cardErr && <div className="form-error">{cardErr}</div>}
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <button className="btn btn-primary"
              disabled={saveCard.isPending || draft.holder.trim() === '' || draft.last4.length !== 4}
              onClick={submitCard}>
              {saveCard.isPending ? 'กำลังบันทึก…' : 'บันทึกบัตร'}
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              เลขท้ายบัตรใช้จับคู่รายการจากการเดินบัญชีที่ช่องทางเป็นบัตร
            </span>
          </div>
        </div>
      )}

      {cardsQ.isLoading ? <Skel h={220} /> : rows.length === 0 ? (
        <Empty title="ยังไม่มีบัตรในทะเบียน" body="เพิ่มบัตรใบแรกเพื่อให้ระบบตรวจการใช้จ่ายตามนโยบาย" />
      ) : (
        <div className="table-wrap card">
          <table className="data">
            <thead>
              <tr>
                <th>ผู้ถือบัตร</th>
                <th>บัตร</th>
                <th className="num">วงเงินต่อรายการ</th>
                <th>สถานะ</th>
                <th>วันที่ออก</th>
                <th className="num">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>{c.holder}</td>
                  <td className="mono dim">••••{c.last4}</td>
                  <td className="num"><Money satang={c.limit_satang} /></td>
                  <td>
                    <span className="chip" style={{ color: STATUS_TONE[c.status], borderColor: 'currentColor' }}>
                      {STATUS_LABEL[c.status]}
                    </span>
                  </td>
                  <td className="dim">{c.issued_on ? thaiDate(c.issued_on) : '—'}</td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      {canWrite && (
                        <button className="btn btn-ghost btn-sm" onClick={() => startEdit(c)}>แก้ไข</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="card-label">นโยบายร้านค้าและเวลา</div>
        <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>
          ใช้กับทุกรายการบัตรขององค์กร วงเงินต่อรายการจะถูกใช้ร่วมกับวงเงินบัตร โดยระบบเลือกเพดานที่ต่ำกว่า
        </div>
        <div className="grid-2" style={{ marginTop: 14 }}>
          <div className="field" style={{ marginTop: 0, gridColumn: '1 / -1' }}>
            <label htmlFor="mcc">หมวดร้านค้าที่ห้าม (MCC)</label>
            <div className="row" style={{ gap: 8 }}>
              <input id="mcc" className="input mono" inputMode="numeric" maxLength={4} value={mccInput}
                disabled={!canWrite}
                onChange={(e) => setMccInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMCC() } }}
                placeholder="7995" style={{ maxWidth: 160 }} />
              <button className="btn btn-ghost" disabled={!canWrite || mccInput.trim() === ''} onClick={addMCC}>
                เพิ่มหมวด
              </button>
            </div>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              {policyDraft.blocked_mcc.length === 0 ? (
                <span className="dim" style={{ fontSize: 12 }}>ยังไม่มีหมวดที่ห้าม</span>
              ) : policyDraft.blocked_mcc.map((code) => (
                <span key={code} className="chip mono">
                  {code}
                  {canWrite && (
                    <button type="button" aria-label={`ลบหมวด ${code}`} onClick={() => removeMCC(code)}
                      style={{ background: 'none', border: 0, color: 'inherit', cursor: 'pointer', padding: 0, marginLeft: 6, font: 'inherit' }}>
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>

          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="night-start">ช่วงเวลาที่ห้ามใช้บัตร</label>
            <div className="row" style={{ gap: 8 }}>
              <select id="night-start" className="select" style={{ width: 110 }} disabled={!canWrite}
                value={policyDraft.night_start_hour}
                onChange={(e) => { setPolicySaved(false); setPolicyDraft({ ...policyDraft, night_start_hour: Number(e.target.value) }) }}>
                {HOURS.map((h) => <option key={h} value={h}>{pad2(h)}:00</option>)}
              </select>
              <span className="dim">ถึง</span>
              <select className="select" style={{ width: 110 }} disabled={!canWrite}
                aria-label="ชั่วโมงสิ้นสุด"
                value={policyDraft.night_end_hour}
                onChange={(e) => { setPolicySaved(false); setPolicyDraft({ ...policyDraft, night_end_hour: Number(e.target.value) }) }}>
                {HOURS.map((h) => <option key={h} value={h}>{pad2(h)}:00</option>)}
              </select>
            </div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
              ถ้าเวลาเริ่มมากกว่าเวลาสิ้นสุด ระบบถือเป็นช่วงข้ามคืน เช่น 21:00 ถึง 07:00
            </div>
          </div>

          <div className="field" style={{ marginTop: 0 }}>
            <label htmlFor="txn-limit">วงเงินต่อรายการ (บาท)</label>
            <input id="txn-limit" className="input mono" inputMode="decimal" value={limitText} disabled={!canWrite}
              onChange={(e) => { setPolicySaved(false); setLimitText(e.target.value.replace(/[^0-9.]/g, '').slice(0, 15)) }}
              placeholder="50,000.00" />
            <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
              เพดานสูงสุดต่อหนึ่งรายการก่อนถือว่าเกินนโยบาย
            </div>
          </div>
        </div>
        {policyErr && <div className="form-error">{policyErr}</div>}
        <div className="row" style={{ gap: 12, marginTop: 14 }}>
          {canWrite && (
            <button className="btn btn-primary" disabled={savePolicy.isPending} onClick={submitPolicy}>
              {savePolicy.isPending ? 'กำลังบันทึก…' : 'บันทึกนโยบาย'}
            </button>
          )}
          {policySaved && <span className="dim" style={{ fontSize: 11.5 }}>บันทึกนโยบายแล้ว</span>}
          {!canWrite && <span className="dim" style={{ fontSize: 11.5 }}>คุณมีสิทธิ์อ่านนโยบายแต่ไม่มีสิทธิ์แก้ไข</span>}
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="card-label">สแกนการใช้บัตร</div>
        <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <select className="select" style={{ minWidth: 280 }} value={engagementID} disabled={engagements.length === 0}
            aria-label="โครงการตรวจสอบ"
            onChange={(e) => setScanEngagement(e.target.value)}>
            {engagements.length === 0 && <option value="">ยังไม่มีโครงการตรวจสอบ</option>}
            {engagements.map((e) => <option key={e.id} value={e.id}>{e.code} · {e.title}</option>)}
          </select>
          <button className="btn btn-primary" disabled={!engagementID || scan.isPending}
            onClick={() => { setScanErr(''); scan.mutate(engagementID) }}>
            {scan.isPending ? 'กำลังสแกน…' : 'สแกนบัตร'}
          </button>
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
          ตรวจวงเงิน หมวดร้านค้าที่ห้าม ช่วงเวลาที่ห้ามใช้ บัตรที่ถูกระงับ และรายการยอดเล็กถี่ผิดปกติ
        </div>
        {scanErr && <div className="form-error">{scanErr}</div>}

        {scan.data && (
          <div style={{ marginTop: 16 }}>
            <div className="row" style={{ gap: 18, flexWrap: 'wrap' }}>
              <span>สแกน {scan.data.scanned} รายการ จากบัตร {scan.data.cards} ใบ</span>
              <span className="dim">พบ {scan.data.hits.length} รายการ · บันทึกใหม่ {scan.data.created} · ข้ามรายการเดิม {scan.data.skipped}</span>
            </div>
            {scan.data.hits.length === 0 ? (
              <div style={{ marginTop: 12 }}>
                <Empty title="ไม่พบรายการผิดนโยบาย" body="รายการบัตรในช่วงนี้อยู่ในวงเงิน หมวดที่อนุญาต และเวลาที่กำหนด" />
              </div>
            ) : (
              <div className="stack" style={{ marginTop: 12, gap: 10 }}>
                {scan.data.hits.map((h, i) => (
                  <div key={`${h.rule_code}-${i}`} className="card card-pad" style={{ background: 'var(--color-bg-soft)' }}>
                    <div className="row" style={{ justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
                      <div>
                        <div className="row" style={{ gap: 10 }}>
                          <Severity value={h.severity} />
                          <span className="mono dim" style={{ fontSize: 11 }}>{h.rule_code}</span>
                        </div>
                        <div style={{ marginTop: 8 }}>{h.title}</div>
                        <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>{h.summary}</div>
                      </div>
                      <div className="num" style={{ textAlign: 'right' }}>
                        <Money satang={h.amount_at_risk_satang} />
                        <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>ความเสี่ยง {h.risk_score}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
